import { Box, TableCell, TableRow, Typography } from "@mui/material";
import moment from "moment";
import { createFormData } from "nhb-toolbox";
import { useEffect, useState } from "react";
import type { UseFormReset } from "react-hook-form";
import TableActions from "src/common/TableActions";
import ModalsWrapper from "src/components/Modals/ModalsWrapper";
import { useApiMutation } from "src/hooks/useApiMutation";
import { useValidImage } from "src/hooks/useValidImage";
import { IAdminFair } from "src/Types/module/fair/adminFair";
import { getStatusLabelFairList } from "src/Utils/Combined";
import DeleteFair from "./DeleteFair";
import FairForm from "./FairForm";
import type { IFairUpdate } from "./type/fairType";

type IProps = {
	fair: IAdminFair;
	serial: number;
};

const FairTableRow = ({ fair, serial }: IProps) => {
	const [openDialogue, setOpenDialogue] = useState(false);
	const [openDeleteDialogue, setOpenDeleteDialogue] = useState(false);

	const {
		mutate: updateFair,
		isPending,
		isSuccess,
	} = useApiMutation<FormData, void>({
		method: "PATCH",
		endPoint: `/api/fair/${fair.fair_id}`,
		invalidateQueries: ["Fairs"],
	});

	const imgSrc = useValidImage(fair.fair_main_banner);
	const venue_layout = useValidImage(fair.fair_venue_layout);

	const handleUpdateFair = (
		data: IFairUpdate,
		reset: UseFormReset<IFairUpdate>
	) => {
		const formData = createFormData(data);
		updateFair(formData);
	};

	useEffect(() => {
		if (isSuccess) {
			setOpenDialogue(false);
		}
	}, [isSuccess]);

	return (
		<TableRow hover>
			<TableCell>
				<Typography
					variant='body1'
					color='text.primary'
					gutterBottom
					noWrap
				>
					{serial}
				</Typography>
			</TableCell>
			<TableCell>
				<Box sx={{ display: "flex" }}>
					<img
						width={30}
						height={30}
						style={{
							borderRadius: "50%",
							marginRight: "10px",
						}}
						src={imgSrc}
						alt={fair.fair_name}
					/>
					<Typography
						variant='body1'
						color='text.primary'
						gutterBottom
						noWrap
					>
						{fair.fair_name}
					</Typography>
				</Box>
			</TableCell>
			<TableCell>
				{
					<img
						width={30}
						height={30}
						style={{
							borderRadius: "50%",
							marginRight: "10px",
						}}
						src={venue_layout}
						alt={fair.fair_name}
					/>
				}
			</TableCell>
			<TableCell>{getStatusLabelFairList(fair.fair_status)}</TableCell>
			<TableCell>
				<Typography
					variant='body1'
					color='text.primary'
					gutterBottom
					noWrap
				>
					{moment(fair.fair_start_date).format("LL")}
				</Typography>
			</TableCell>
			<TableCell>
				<Typography
					variant='body1'
					color='text.primary'
					gutterBottom
					noWrap
				>
					{moment(fair.fair_end_date).format("LL")}
				</Typography>
			</TableCell>
			<TableCell>
				<Typography
					variant='body1'
					color='text.primary'
					gutterBottom
					noWrap
				>
					{fair.fair_location}
				</Typography>
			</TableCell>
			<TableActions
				showEdit
				showView
				deleteElement
				showDelete
				viewLink={`/fair/list/${fair.fair_id.toString()}`}
				handleEditFun={() => setOpenDialogue(true)}
				handleDeleteFun={() => setOpenDeleteDialogue(true)}
			/>

			<ModalsWrapper
				setShowModal={setOpenDialogue}
				modalData={{ title: `Update ${fair?.fair_name}` }}
				showModal={openDialogue}
			>
				<FairForm
					isLoading={isPending}
					onSubmit={handleUpdateFair}
					previousData={fair}
					EditMode
				/>
			</ModalsWrapper>

			<ModalsWrapper
				setShowModal={setOpenDeleteDialogue}
				modalData={{ title: `Delete ${fair?.fair_name}?` }}
				showModal={openDeleteDialogue}
			>
				<DeleteFair id={fair.fair_id} name={fair?.fair_name} />
			</ModalsWrapper>
		</TableRow>
	);
};

export default FairTableRow;
